import type { TranslationCatalog } from "../types.js";

// Hungarian nouns stay in singular after a numeral ("5 érme", not "5 érmék"),
// so no count-dependent form is actually needed here — kept as a function
// only to match the catalog's shape.
function coinWord(_n: number): string {
  return "érme";
}

export default {
  "common.loading": "Csatlakozás...",
  "common.loadingEllipsis": "Betöltés...",
  "common.unknownError": "Ismeretlen hiba",
  "common.errorPrefix": (p: { message: string }) => `Hiba: ${p.message}`,
  "common.retry": "Próbáld újra",
  "common.close": "Bezárás",
  "common.back": "← Vissza",
  "common.cancel": "Mégse",
  "common.details": "Részletek",
  "common.settingsTitle": "⚙️ Beállítások",
  "common.profile": "👤 Profil",
  "common.rules": "📖 Szabályok",

  "login.subtitle": "Jelentkezz be a játékhoz.",
  "login.continueFacebook": "Folytatás Facebook-fiókkal",
  "login.continueGoogle": "Folytatás Google-fiókkal",
  "login.continueEmail": "Folytatás e-mail címmel",
  "login.tagline": "Licites, adus kártyajáték három játékosnak. Licitálj, válassz adut és vidd az ütéseket — aki elsőként eléri a 101 pontot, nyer.",
  "login.linkRules": "Szabályok",
  "login.linkStrategy": "Stratégia",
  "login.linkAbout": "A játékról",
  "login.linkPrivacy": "Adatvédelem",

  "lobby.entryFee": (p: { fee: number }) => `Belépő partinként: ${p.fee} ${coinWord(p.fee)}`,
  "lobby.resumeMatch": "Parti folytatása",
  "lobby.playButton": "Játék (3 játékos)",
  "lobby.inviteFriend": "📣 Barát meghívása",
  "lobby.insufficientFunds": (p: { missing: number }) => `Nincs elég érméd a belépőhöz (${p.missing} hiányzik).`,
  "lobby.watchAd": "Nézz reklámot több érméért",

  "leaderboard.title": "🏆 Ranglista",
  "leaderboard.emptySeason": "Még nincs rangsorolt játékos ebben a szezonban (legalább 1 parti szükséges).",
  "leaderboard.emptyGlobal": "Még nincs rangsorolt játékos (legalább 1 parti szükséges összesen).",
  "leaderboard.seasonTabDefault": "Szezon",
  "leaderboard.seasonTabWithDate": (p: { month: string; year: string }) => `Szezon ${p.month}/${p.year}`,
  "leaderboard.globalTab": "🌍 Összesen (minden szezon)",
  "leaderboard.colPlayer": "Játékos",
  "leaderboard.colPoints": "Pontok",
  "leaderboard.colMatches": "Partik",
  "leaderboard.you": "Te",
  "leaderboard.yourScoreUnranked": (p: { points: number; matchesPlayed: number }) =>
    `Eredményed: ${p.points} pont ${p.matchesPlayed} partiból (legalább 1 parti kell a rangsoroláshoz)`,

  "rules.title": "📖 Hogyan kell Meksikót játszani",
  "rules.cardsHeading": "Kártyák",
  "rules.cardsBody":
    "32 lapos paklival játsszák (7, 8, 9, 10, J, Q, K, A mind a 4 színben). A lapok erőssége színen belül, a leggyengébbtől: 7 &lt; 8 &lt; 9 &lt; 10 &lt; J &lt; Q &lt; K &lt; A. Az adu szín minden nem-adu lapot visz, rangtól függetlenül.",
  "rules.dealingHeading": "Osztás",
  "rules.dealingBody": 'Mindhárom játékos 10 lapot kap (két körben, körönként 5-öt), 2 lap pedig rejtve marad a "talonban".',
  "rules.biddingHeading": "Licitálás",
  "rules.biddingBody":
    'Az első játékosnak legalább 5-öt kell licitálnia. Minden következőnek szigorúan magasabbat kell licitálnia (5, 6, 7, 8, 9, 10, vagy "MEKSIKO"), vagy azt kell mondania, hogy "passz" — aki egyszer passzol, végleg kiesik a licitből abban a leosztásban. A licit akkor ér véget, amikor már csak egy játékos marad.',
  "rules.talonHeading": "Talon és lerakás",
  "rules.talonBody":
    "A licit győztese felfedi a talont (2 lap, mindenki látja), hozzáadja a kezéhez (most 12 lapja van), majd lerak 2 lapot lefelé fordítva. Ezután választ adu színt.",
  "rules.mexicoHeading": "MEKSIKO (20)",
  "rules.mexicoBody":
    'Aki "MEKSIKO"-t licitál, vállalja, hogy megnyeri mind a 10 ütést. Adu nélkül és talon nélkül játsszák — csak az eredeti 10 lappal.',
  "rules.tricksHeading": "Játékmenet (ütések)",
  "rules.tricksBody":
    "Ha van belőle lapod, követned kell a kijátszott színt. Ha nincs, adut kell dobnod, ha van (vágás) — csak ha egyik sincs, dobhatsz bármit. Az ütést a kijátszott szín legerősebb lapja viszi, hacsak valaki nem dobott adut — ekkor a legerősebb adu visz, színtől függetlenül. Aki viszi az ütést, kezd a következőben.",
  "rules.scoringHeading": "Pontozás",
  "rules.scoringBody":
    "A licit győztese: ha legalább annyi ütést visz, amennyit licitált, annyi pontot kap, amennyit ténylegesen vitt. Ha nem sikerül neki, a licit értékének megfelelő <strong>negatív</strong> pontot kap. A másik két játékos mindig annyi pontot kap, ahány ütést vitt.",
  "rules.matchEndHeading": "A parti vége",
  "rules.matchEndBody":
    "A parti akkor ér véget, amikor valaki eléri a célpontszámot. Ha több játékos is átlépi a célt ugyanabban a leosztásban azonos legmagasabb eredménnyel, egy újabb leosztást játszanak, amíg el nem dől.",

  "settings.soundOff": "🔇 Hang kikapcsolva",
  "settings.soundOn": "🔊 Hang bekapcsolva",
  "settings.displayNameLabel": "Játékbeli név",
  "settings.saveDisplayName": "Név mentése",
  "settings.displayNameLengthError": "A névnek 1-20 karakter hosszúnak kell lennie.",
  "settings.saveFailed": "A mentés sikertelen, próbáld újra.",
  "settings.logout": "🚪 Kijelentkezés",

  "waitingRoom.waitingForPlayers": (p: { filled: number; total: number }) => `Játékosokra várakozás... ${p.filled}/${p.total}`,
  "waitingRoom.countdown": (p: { remainingSec: number }) => `${p.remainingSec} másodperc múlva az AI tölti ki az üres helyeket`,

  "game.passLabel": "passz",
  "game.passButton": "Passz",
  "game.mexicoLabel": "MEKSIKO",
  "game.bidLabel": (p: { bid: string }) => `Licit: ${p.bid}`,
  "game.trumpLabel": (p: { suit: string }) => `Adu: ${p.suit}`,
  "game.tricksWonLabel": (p: { count: number }) => `Ütések: ${p.count}`,
  "game.biddingInProgress": "Licitálás folyamatban...",
  "game.yourBid": "A te licited",
  "game.someoneDiscarding": (p: { name: string }) => `${p.name} lerak lapokat...`,
  "game.someoneChoosingTrump": (p: { name: string }) => `${p.name} adut választ...`,
  "game.chooseTrumpPrompt": "Válassz adut",
  "game.nextDealPause": "Következő leosztás...",
  "game.fromTalon": "a talonból",
  "game.talonRevealLabel": (p: { bidder: string }) => `Talonlapok (${p.bidder})`,
  "game.yourTurnLabel": "Te jössz",
  "game.turnTimerLabel": (p: { name: string; remainingSec: number }) => `${p.name} — ${p.remainingSec}s`,
  "game.idleBotTakeover": "🤖 Nem léptél időben, így most az AI játszik helyetted. Kattints bárhová, hogy visszavedd az irányítást.",
  "game.leaveMatch": "Parti elhagyása",
  "game.cardAlt": "kártya",
  "game.botDisplayName": "AI játékos",
  "game.botBadgeIdle": "🤖 AI (ideiglenes)",
  "game.botBadgeFull": "🤖 BOT",
  "game.declarerTooltip": "Vezeti a licitet",

  "matchEnd.title": "Vége a partinak",
  "matchEnd.winner": (p: { name: string }) => `Győztes: ${p.name}`,
  "matchEnd.backToLobby": "Vissza a lobbiba",

  "reactions.sendButton": "Reakció küldése",
  "reaction.angry_small_card": "Miért kis lappal?",
  "reaction.angry_big_card": "Miért nagy lappal?",
  "reaction.bravo": "Bravó",
  "reaction.svaka_cast": "Szép volt",
  "reaction.cestitam_pobeda": "Gratulálok a győzelemhez",
  "reaction.izvini": "Bocsánat",
  "reaction.pa_zasto": "De miért!",
  "reaction.srecan": "",
  "reaction.srce": "",
  "reaction.pozdrav": "",

  "medal.season_gold": "🥇 Szezon",
  "medal.season_silver": "🥈 Szezon",
  "medal.season_bronze": "🥉 Szezon",
  "medal.first_match": "🎉 Első parti",
  "medal.matches_100": "💯 100 parti",
  "medal.mexico_master": "🌵 Meksiko mester",
  "medal.hat_trick_3_wins": "🔥 Mesterhármas (3 győzelem)",
  "medal.tricks_1000": "🃏 1000 ütés",

  "suit.diamonds": "Káró",
  "suit.hearts": "Kőr",
  "suit.clubs": "Treff",
  "suit.leaves": "Levél",

  "profile.matchesPlayed": "Lejátszott partik",
  "profile.firstPlace": (p: { wins: number }) => `🥇 1. hely (${p.wins})`,
  "profile.secondPlace": (p: { count: number }) => `🥈 2. hely (${p.count})`,
  "profile.thirdPlace": (p: { count: number }) => `🥉 3. hely (${p.count})`,
  "profile.pointsConfigNote": (p: { win: number; second: number; third: number }) =>
    `Pontozás partinként: 1. hely ${p.win}, 2. hely ${p.second}, 3. hely ${p.third} pont`,
  "profile.noMedals": "Még nincs megszerzett érmed.",
  "profile.medalsHeading": "Érmek",
  "profile.seasonHistoryHeading": "Szezontörténet",
  "profile.colSeason": "Szezon",
  "profile.colRank": "Helyezés",
  "profile.deleteAccountButton": "🗑️ Fiók törlése",
  "profile.deleteAccountTitle": "Fiók törlése",
  "profile.deleteAccountWarning":
    "Ez véglegesen törli a fiókodat és minden adatodat (mérkőzés-előzményeidet, ranglistahelyezésedet, kitüntetéseidet, éremegyenlegedet). Ez a művelet nem vonható vissza.",
  "profile.deleteAccountConfirm": "Igen, törlöm a fiókot",
  "profile.deleteAccountFailed": "A törlés sikertelen, próbáld újra.",
  "profile.deleteAccountActiveMatch": "Nem törölheted a fiókot, amíg aktív mérkőzésben vagy. Fejezd be vagy hagyd el a mérkőzést, majd próbáld újra.",

  "errors.NOT_YOUR_TURN": "Nem te jössz.",
  "errors.INVALID_BID": "Érvénytelen licit.",
  "errors.BID_TOO_LOW": "A licitnek magasabbnak kell lennie a jelenlegi legmagasabbnál.",
  "errors.MUST_BID_FIRST": "Az első játékosnak legalább 5-öt kell licitálnia, nem passzolhat.",
  "errors.DUPLICATE_DISCARD": "Nem dobhatod le kétszer ugyanazt a lapot.",
  "errors.CARD_NOT_IN_HAND": "Ez a lap nincs a kezedben.",
  "errors.MUST_FOLLOW_SUIT": "Követned kell a színt, vagy adut kell vágnod, ha nem tudod.",
  "errors.TRICK_INCOMPLETE": "Az ütés még nem fejeződött be.",
  "errors.MATCH_ENDED": "A parti már véget ért.",
  "errors.UNKNOWN_ACTION": "Ismeretlen művelet.",
  "errors.WRONG_PHASE": "Ez a lépés nem megengedett a játék ezen szakaszában.",
  "errors.NOT_BID_WINNER": "Csak a licit győztese teheti ezt meg.",
  "errors.MATCH_NOT_ENDED": "A parti még nem ért véget.",
  "errors.invalid_session_token": "Lejárt a munkamenet, jelentkezz be újra.",
  "errors.room_full": "Az asztal tele van, próbáld újra egy perc múlva.",
  "errors.insufficient_balance": "Nincs elég érméd a belépőhöz.",
  "errors.ad_unavailable": "A hirdetés jelenleg nem elérhető. Ellenőrizd az internetkapcsolatot (vagy kapcsold ki az AdBlockot), és próbáld újra.",
  "errors.invalid_or_early_ticket": "A hirdetést nem sikerült megtekintettként rögzíteni. Próbáld újra.",
  "errors.daily_ad_limit_reached": "Elérted a mai hirdetés-limitet. Próbáld holnap.",

  "share.inviteText": "Játssz Meksikót velem!",
  "ad.placeholderText": "Szimulált reklám (standalone web demó)",
  "rotateOverlay.text": "Fordítsd el a telefont a Meksiko játékhoz",
  "languageSwitcher.ariaLabel": "Nyelv",
} satisfies TranslationCatalog;
